import { useState } from "react";
import { SERVICES } from "../data/products";
import {
  Activity,
  ShieldCheck,
  ChevronDown,
  CheckCircle,
  ClipboardList,
  PenTool,
} from "lucide-react";
import { Box, Typography, Stack } from "@mui/joy";

export default function EngineeringServices() {
  const [expandedId, setExpandedId] = useState(null);

  // Map service icons
  const iconMap = {
    ShieldAlert: ClipboardList,
    CheckCircle: ShieldCheck,
    Activity: Activity,
    Settings: PenTool,
  };
  const themeColors = {
    obsidian: "#0b0f19",
    safety: "#f97316", // fallback orange/safety color
    ink: "#111827",
    concrete: "#f3f4f6",
    mutedForeground: "#6b7280",
  };
  return (
    <Box
      component="section"
      id="services"
      sx={{
        py: { xs: "80px", md: "112px" }, // Increased vertical padding
        backgroundColor: "#f8fafc", // bg-slate-50
        scrollMarginTop: "3rem",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px", // max-w-7xl
          mx: "auto",
          px: { xs: "16px", sm: "24px", lg: "32px" },
        }}
      >
        {/* Section Title */}
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "56rem", // Increased max-width for title area
            mx: "auto",
            mb: "80px", // Increased bottom margin
          }}
        >
          <Typography
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            sx={{
              fontFamily: "monospace",
              color: themeColors.safety,
              mb: 4,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            [ Technical Assistance & AMC ]
          </Typography>
          <Typography
            level="h2"
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif;",
              fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.25rem" }, // Increased title size
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "#0f172a",
              mt: "24px",
              mb: "20px",
            }}
          >
            Industrial Engineering Services
          </Typography>
          <Box
            sx={{
              width: "5rem", // Increased line width
              height: "0.375rem", // Increased line thickness
              backgroundColor: "#2563eb",
              mx: "auto",
              mb: "32px",
              borderRadius: "9999px",
            }}
          />
          <Typography
            sx={{
              fontFamily: "sans-serif",
              color: "#64748b",
              lineHeight: 1.75,
              fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" }, // Increased subtitle size
            }}
          >
            Beyond manufacturing, we support critical power installations with
            Annual Maintenance Contracts (AMC), on-site diagnostic testing,
            emergency repairs, and full site-commissioning.
          </Typography>
        </Box>

        {/* Layout Container - Centered and single column */}
        <Box
          sx={{
            maxWidth: "900px", // Constrains the list width for readability
            mx: "auto", // Centers the list in the middle of the screen
            width: "100%",
          }}
        >
          <Stack spacing="24px">
            {" "}
            {/* Increased spacing between cards */}
            {SERVICES.map((srv) => {
              const Icon = iconMap[srv.icon] || CheckCircle;
              const isExpanded = expandedId === srv.id;

              return (
                <Box
                  key={srv.id}
                  onClick={() => setExpandedId(isExpanded ? null : srv.id)}
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: "1.25rem", // Slightly larger border radius
                    border: "1px solid",
                    borderColor: isExpanded ? "#2563eb" : "#f1f5f9",
                    boxShadow: isExpanded
                      ? "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)" // Stronger shadow
                      : "none",
                    boxShadowColor: isExpanded
                      ? "rgba(37, 99, 235, 0.15)"
                      : "transparent",
                    transition: "all 0.3s",
                    overflow: "hidden",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: isExpanded ? "#2563eb" : "#e2e8f0",
                      transform: isExpanded ? "none" : "translateY(-2px)", // Gentle lift on hover
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: "24px", sm: "32px" }, // Increased padding
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "24px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "24px", // Increased gap between icon and text
                      }}
                    >
                      <Box
                        sx={{
                          p: "16px", // Increased icon container padding
                          borderRadius: "1rem",
                          transition: "background-color 0.3s, color 0.3s",
                          backgroundColor: isExpanded ? "#2563eb" : "#f1f5f9",
                          color: isExpanded ? "#ffffff" : "#64748b",
                        }}
                      >
                        <Icon style={{ width: "28px", height: "28px" }} />{" "}
                        {/* Increased icon size */}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily:
                              "Cabinet Grotesk, system-ui, sans-serif;",
                            fontWeight: "bold",
                            fontSize: { xs: "1.125rem", sm: "1.25rem" }, // Increased card title size
                            color: "#0f172a",
                          }}
                        >
                          {srv.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" }, // Increased description size
                            color: "#94a3b8",
                            mt: "4px",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {srv.description}
                        </Typography>
                      </Box>
                    </Box>
                    <ChevronDown
                      style={{
                        width: "24px",
                        height: "24px", // Increased chevron size
                        color: "#94a3b8",
                        transition: "transform 0.3s",
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        flexShrink: 0,
                      }}
                    />
                  </Box>

                  {/* Expanded Content Details */}
                  {isExpanded && (
                    <Box
                      sx={{
                        px: { xs: "24px", sm: "32px" },
                        pb: "32px",
                        pt: "16px",
                        borderTop: "1px solid #f8fafc",
                        backgroundColor: "rgba(248, 250, 252, 0.5)",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.8125rem", // ~13px, up from 11px
                          fontFamily: "monospace",
                          fontWeight: "bold",
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          mb: "12px",
                        }}
                      >
                        Service Scope Includes:
                      </Typography>
                      <Stack
                        component="ul"
                        spacing="12px" // Increased list spacing
                        sx={{ p: 0, m: 0, listStyle: "none" }}
                      >
                        {srv.details.map((detail, index) => (
                          <Box
                            component="li"
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              fontSize: "0.9375rem", // ~15px, up from 12px
                              color: "#475569",
                              lineHeight: 1.625,
                              fontFamily: "sans-serif",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: "8px", // Increased bullet size
                                height: "8px",
                                borderRadius: "9999px",
                                backgroundColor: "#2563eb",
                                mt: "8px",
                                flexShrink: 0,
                              }}
                            />
                            <span>{detail}</span>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
